import React, { Suspense, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import NoSsr from '@material-ui/core/NoSsr';

// app modules: components
import Advert from 'components/Advert';
import ErrorBoundary from 'components/ErrorBoundary';
import HouseAd from 'components/HouseAd';
import Link from 'components/Link';
import NestedContent from 'components/NestedContent';
import RelatedContentQuery from 'components/RelatedContentQuery';

import showIntercomMessagesHelper from 'api/helpers/showIntercomMessagesHelper';

// app modules: services
import route from 'services/route';

// app modules: props
import AuthorsProps from 'components/Authors/AuthorsProps';
import NestedContentProps from 'components/NestedContent/NestedContentProps';
import VideosProps from 'components/Videos/VideosProps';

// assets
import CssRecipe from 'components/Recipe/Recipe.scss';
import CssContentExtra from './ContentExtra.scss';

const Comments = React.lazy(() => import(/* webpackChunkName: 'comments' */ 'components/Comments'));
const Videos = React.lazy(() => import(/* webpackChunkName: 'videos' */ 'components/Videos'));
function ContentExtra(props) {
  const {
    authors,
    book,
    hash,
    forceSsr,
    id,
    isPreview,
    isVariation,
    nestedContent,
    parentsNestedContents,
    next,
    parents,
    prev,
    title,
    type,
    userAuthorId,
    videos,
    withComments,
    setUserRecipeReview,
    hasPrimaryImage,
  } = props;
  useStyles(CssContentExtra, CssRecipe);
  const [backlinksLoaded, setBacklinksLoaded] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(null);
  const urlHash = useSelector(state => state.router.hash);

  useEffect(
    () => {
      setBacklinksLoaded(false);
      setCommentsLoaded(false);
    },
    [id],
  );

  const viewingAsAuthor =
    userAuthorId &&
    (authors?.some(author => author.id === userAuthorId) ||
      book?.authors?.some(author => author.id === userAuthorId));

  const handleProblemClick = evt => {
    const message = `Feedback on ${title.join(' ')} from ${book.title}: `;
    if (window.Intercom) {
      evt.preventDefault();
      window.Intercom('showNewMessage', message);
    } else if (window.intercom) {
      evt.preventDefault();
      showIntercomMessagesHelper(message);
    }
  };

  const inThisSectionHeading = useMemo(
    () => {
      let heading = 'In this section';
      if (nestedContent?.every(item => item.is_variation)) {
        if (nestedContent.length === 1) {
          heading = 'Variation';
        } else {
          heading = 'Variations';
        }
      }
      return heading;
    },
    [nestedContent],
  );

  const isComponents = useMemo(
    () =>
      (inThisSectionHeading === 'In this section' && type === 'Recipe') ||
      (parentsNestedContents && parentsNestedContents.length),
    [inThisSectionHeading, parentsNestedContents, type],
  );

  const componentsContent = useMemo(
    () =>
      nestedContent && nestedContent.length
        ? [
            {
              title,
              id,
              isLink: false,
              index: 0,
            },
            ...nestedContent.map(value => ({ ...value, isLink: true })),
          ]
        : parentsNestedContents.map(value => ({
            ...value,
            isLink: id !== value.id,
          })),
    [nestedContent, parentsNestedContents, title, id],
  );

  const showFeedbackLink = !isPreview;

  const filterVariationContent = (isVariationContent, content) =>
    isVariationContent ? [content[0]] : content;

  return (
    <React.Fragment>
      {isVariation ? (
        <NestedContent
          title="Variation of"
          content={filterVariationContent(isVariation, parents)}
        />
      ) : null}
      {inThisSectionHeading.includes('Variation') && !isComponents ? (
        <NestedContent title={inThisSectionHeading} content={nestedContent} />
      ) : null}
      {isComponents ? (
        <NestedContent title="Components" isComponents content={componentsContent} />
      ) : null}
      {videos?.length ? (
        <NoSsr>
          <Suspense fallback={null}>
            <div className="content-extra__video">
              <Videos videos={videos} className="margin-bottom-40" />
            </div>
          </Suspense>
        </NoSsr>
      ) : null}
      <nav className="content-extra__next-prev do-not-print">
        <div className="content-extra__prev">
          {prev?.id ? (
            <Link to={route.content(prev.id, prev.title)} state={{ animate: true }}>
              <div className="heading">« Previous</div>
              <span className="content-extra__title">{prev.title}</span>
            </Link>
          ) : null}
        </div>
        <div className="content-extra__next">
          {next?.id ? (
            <Link to={route.content(next.id, next.title)} state={{ animate: true }}>
              <div className="heading">Next »</div>
              <span className="content-extra__title">{next.title}</span>
            </Link>
          ) : null}
        </div>
      </nav>
      {!inThisSectionHeading.includes('Variation') && !isComponents ? (
        <NestedContent title={inThisSectionHeading} content={nestedContent} />
      ) : null}
      {!isVariation || parentsNestedContents.length ? (
        <NestedContent
          title="Part of"
          indent
          content={filterVariationContent(isVariation, parents)}
        />
      ) : null}

      <div className="margin-top-40">
        <RelatedContentQuery
          // Lazy loading gets disabled when there's a hash to allow for accurate scrolling to the comments section below
          lazy={!urlHash}
          dualColumn
          forceSsr={forceSsr}
          recipeId={id}
          type={type}
          query="backlinks"
          onLoad={() => setBacklinksLoaded(true)}
        />
      </div>
      {withComments ? (
        <NoSsr>
          <div className="margin-top-40 margin-bottom-40">
            <Suspense fallback={null}>
              <ErrorBoundary>
                <Comments
                  hasPrimaryImage={hasPrimaryImage}
                  readyToScroll={backlinksLoaded && commentsLoaded}
                  onLoad={() => setCommentsLoaded(true)}
                  id={id}
                  hash={hash}
                  setUserRecipeReview={setUserRecipeReview}
                  asAuthor={viewingAsAuthor}
                />
              </ErrorBoundary>
            </Suspense>
          </div>
        </NoSsr>
      ) : null}
      {book.house_ad ? (
        <HouseAd
          format={book.house_ad.format}
          href={book.house_ad.href}
          imageSrc={book.house_ad.image_src}
          mobileFormat={book.house_ad.mobile_format}
          mobileImageSrc={book.house_ad.mobile_image_src}
        />
      ) : (
        <Advert ezoicPlaceholderId={103} lazy className="margin-top-30" slotName="recipe" />
      )}
      {type === 'Section' ? null : (
        <div className="recipe__columns">
          <section className="recipe__column margin-top-40">
            <RelatedContentQuery
              lazy
              forceSsr={forceSsr}
              recipeId={id}
              type={type}
              query="recipes"
            />
          </section>
          <section className="recipe__column margin-top-40">
            <RelatedContentQuery
              lazy
              forceSsr={forceSsr}
              recipeId={id}
              type={type}
              query="references"
            />
          </section>
        </div>
      )}
      {showFeedbackLink ? (
        <p className="recipe__problem margin-top-20 margin-bottom-20">
          <a
            href="https://www.ckbk.com/contact/"
            className="link link--energized"
            onClick={handleProblemClick}
          >
            Spotted a problem? Let us know!
          </a>
        </p>
      ) : null}
      <Advert
        className="margin-top-40"
        slotName="leaderboard-footer"
        format="leaderboard"
        fullWidthResponsive={false}
        ezoicPlaceholderId={102}
      />
    </React.Fragment>
  );
}

ContentExtra.propTypes = {
  authors: AuthorsProps.authors,
  book: PropTypes.shape({
    authors: AuthorsProps.authors,
  }),
  forceSsr: PropTypes.bool,
  hasPrimaryImage: PropTypes.bool,
  hash: PropTypes.string,
  id: PropTypes.string.isRequired,
  isPreview: PropTypes.bool,
  isVariation: PropTypes.bool,
  nestedContent: NestedContentProps.content,
  parentsNestedContents: NestedContentProps.content,
  next: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.arrayOf(PropTypes.string),
  }),
  parents: NestedContentProps.content,
  prev: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.arrayOf(PropTypes.string),
  }),
  title: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.oneOf(['Recipe', 'Encyclopedia', 'Section']).isRequired,
  userAuthorId: PropTypes.number,
  videos: VideosProps.videos,
  withComments: PropTypes.bool,
  setUserRecipeReview: PropTypes.func,
};

ContentExtra.defaultProps = {
  authors: null,
  book: null,
  forceSsr: false,
  hasPrimaryImage: true,
  hash: null,
  isPreview: false,
  isVariation: false,
  nestedContent: [],
  parentsNestedContents: [],
  next: null,
  parents: [],
  prev: null,
  title: null,
  userAuthorId: null,
  videos: [],
  withComments: false,
  setUserRecipeReview: () => {},
};

// Only re-render if the content ID changes. Currently all the other props are immutable for a given recipe.
// If that changes then this will need adjusting accordingly.
export default React.memo(
  ContentExtra,
  (prevProps, nextProps) =>
    prevProps.id === nextProps.id && prevProps.isPreview === nextProps.isPreview,
);
